import React, { useRef, useState, useEffect } from 'react';
import { Button, Modal, Typography, message, Spin, Tooltip } from 'antd';
import { CameraOutlined, ScissorOutlined, CloseOutlined } from '@ant-design/icons';
import { uploadRawImage } from '../api';
import PhoneCaptureButton from '../../../components/PhoneCapture/PhoneCaptureButton';
import ImageCropModal from '../../../components/ImageEditor/ImageCropModal';

const { Text } = Typography;

const thumbSize = 200; // px

const GoldImageCapture = ({
    images = [],
    setImages,
    initials = null,
    customerId = null,
    driveAccessToken = null,
    driveParentFolderId = null,
    driveImageType = 'gold',
    driveFileNameBase = 'gold-image',
    onDriveUploaded = null,
    disabled = false,
    captureTargetKey = 'gold.images',
    captureTargetLabel = 'Gold Images',
    captureFolderPath = '/gold_item',
    captureMaxFiles = 10,
}) => {
    const fileInputRef = useRef(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [stream, setStream] = useState(null);
    const videoRef = useRef(null);
    const imagesRef = useRef(images);

    useEffect(() => {
        imagesRef.current = images;
    }, [images]);

    const openFilePicker = (capture = 'environment') => {
        // Use capture attribute to prefer camera on mobile devices
        if (fileInputRef.current) {
            fileInputRef.current.capture = capture;
            fileInputRef.current.click();
        }
    };

    const pushImage = (entry) => {
        const current = imagesRef.current || [];
        const next = [...current, entry];
        imagesRef.current = next;
        setImages(next);
    };

    const uploadBinary = async (file, { previewSrc, type = 'gold' } = {}) => {
        console.log('[GoldImageCapture] uploadBinary called with file:', file?.name, 'size:', file?.size);
        const dateStr = new Date().toISOString().split('T')[0];
        const fileName = `${customerId}_${dateStr}.jpg`;
        const folderId = 'gold_item';

        console.log('[GoldImageCapture] Calling uploadRawImage with fileName:', fileName, 'folderId:', folderId);
        const response = await uploadRawImage(file, { fileName, folderId });
        console.log('Image Upload response', JSON.stringify(response, null, 2));

        if (!response?.success || !response?.data) {
            throw new Error(response?.message || 'Upload failed');
        }

        const payload = response.data;
        const entry = {
            id: payload.fileId || Math.random().toString(36).slice(2, 9),
            src: previewSrc,
            type,
            fileId: payload.fileId,
            url: payload.url,
            thumbnailUrl: payload.thumbnailUrl,
            filePath: payload.filePath,
            name: payload.name
        };

        pushImage(entry);
        return entry;
    };

    const onFilesSelected = async (e) => {
        const files = Array.from(e.target.files || []);
        console.log('[GoldImageCapture] onFilesSelected called with files:', files.length);
        if (files.length === 0) return;

        try {
            setUploading(true);
            for (const file of files) {
                const previewSrc = URL.createObjectURL(file);
                console.log('[GoldImageCapture] Processing file:', file.name, 'size:', file.size);
                try {
                    await uploadBinary(file, { previewSrc });
                    message.success('Image uploaded');
                } catch (err) {
                    console.error('Image upload failed', err);
                    message.error(err?.message || 'Failed to upload image');
                    if (previewSrc?.startsWith('blob:')) {
                        URL.revokeObjectURL(previewSrc);
                    }
                }
            }
        } finally {
            setUploading(false);
            // reset input so same file can be selected again
            e.target.value = '';
        }
    };

    const handleClick = () => openFilePicker('environment');
    const handleFile = onFilesSelected;

    const preview = (images && images[0] && images[0].src) || null;
    const [actionOpen, setActionOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [cropping, setCropping] = useState(null); // the image being edited

    const openCameraModal = async () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            alert('Camera not supported in this browser');
            return;
        }
        try {
            const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
            setStream(s);
            setCameraOpen(true);
            if (videoRef.current) videoRef.current.srcObject = s;
        } catch (err) {
            console.error('Camera open error', err);
            alert('Unable to access camera: ' + (err?.message || err));
        }
    };

    const handleRemoveFirst = () => {
        const first = images && images[0];
        if (first) removeImage(first.id);
        setActionOpen(false);
    };

    const handleUploadFileFromModal = () => {
        setActionOpen(false);
        openFilePicker('environment');
    };

    const handleUploadCameraFromModal = async () => {
        setActionOpen(false);
        await openCameraModal();
    };

    // Drive upload removed — keep component file/camera handlers only

    const closeCamera = () => {
        if (stream) {
            stream.getTracks().forEach((t) => t.stop());
        }
        setStream(null);
        setCameraOpen(false);
    };

    const captureFromVideo = () => {
        if (!videoRef.current) return;
        const v = videoRef.current;
        const canvas = document.createElement('canvas');
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(async (blob) => {
            if (!blob) return;
            const file = new File([blob], `gold-camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
            const previewSrc = URL.createObjectURL(blob);
            try {
                setUploading(true);
                await uploadBinary(file, { previewSrc });
                message.success('Image uploaded');
            } catch (err) {
                console.error('Camera upload failed', err);
                message.error(err?.message || 'Failed to upload captured image');
                if (previewSrc?.startsWith('blob:')) {
                    URL.revokeObjectURL(previewSrc);
                }
            } finally {
                setUploading(false);
                closeCamera();
            }
        }, 'image/jpeg', 0.95);
    };

    // Files that arrive from the phone are already uploaded to ImageKit by the
    // capture endpoint, so they skip uploadBinary and go straight into the list
    // in the same shape a local upload produces.
    const handlePhoneFiles = (incoming = []) => {
        incoming.forEach((f) => {
            pushImage({
                id: f.fileId || Math.random().toString(36).slice(2, 9),
                src: f.thumbnailUrl || f.url,
                type: 'gold',
                fileId: f.fileId,
                url: f.url,
                thumbnailUrl: f.thumbnailUrl,
                filePath: f.filePath,
                name: f.name,
            });
        });
        if (incoming.length) message.success(`${incoming.length} photo(s) received from phone`);
    };

    // Swap a cropped result in for the image it was made from, keeping its slot
    // in the list. The pre-crop file stays on ImageKit — an edit never destroys
    // the original photo.
    const replaceImage = (id, fileRef) => {
        const list = imagesRef.current || [];
        const next = list.map((img) => (img.id === id ? {
            ...img,
            id: fileRef.fileId || img.id,
            fileId: fileRef.fileId,
            url: fileRef.url,
            thumbnailUrl: fileRef.thumbnailUrl,
            filePath: fileRef.filePath,
            name: fileRef.name,
            src: fileRef.thumbnailUrl || fileRef.url,
        } : img));
        imagesRef.current = next;
        setImages(next);
        setCropping(null);
    };

    const removeImage = (id) => {
        const list = imagesRef.current || [];
        const removed = list.find((i) => i.id === id);
        if (removed?.src && removed.src.startsWith('blob:')) {
            URL.revokeObjectURL(removed.src);
        }
        const remaining = list.filter((i) => i.id !== id);
        imagesRef.current = remaining;
        setImages(remaining);
    };

    return (
        <div style={{ marginTop: 0 }}>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={onFilesSelected}
            />



            <div style={{ position: 'relative', marginTop: 12 }}>
                <div
                    onClick={() => !uploading && !disabled && setActionOpen(true)}
                    role="button"
                    tabIndex={0}
                    style={{
                        width: 200,
                        height: 200,
                        borderRadius: 8,
                        overflow: 'hidden',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.6 : 1,
                        border: '1px solid #e8e8e8',
                        background: '#fafafa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                        color: '#666',
                    }}
                >
                    {preview ? (
                        <img src={preview} alt="customer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    ) : (
                        <div style={{ textAlign: 'center' }}>{initials || <CameraOutlined />}</div>
                    )}
                    {uploading && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(255,255,255,0.85)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Spin size="large" />
                        </div>
                    )}
                </div>

                {/* Kept on the page rather than inside the tile's action sheet:
                    staff should see the phone option without discovering it. */}
                <div style={{ marginTop: 8, width: 200 }}>
                    <PhoneCaptureButton
                        targetKey={captureTargetKey}
                        targetLabel={captureTargetLabel}
                        folderPath={captureFolderPath}
                        recordId={customerId || undefined}
                        maxFiles={captureMaxFiles}
                        onFiles={handlePhoneFiles}
                        disabled={uploading || disabled}
                        buttonText="Capture from Phone"
                        buttonProps={{ block: true, size: 'small' }}
                    />
                </div>
            </div>

            {/* One strip for every uploaded image — the tile alone only ever
                previewed the first, leaving the rest unreachable for editing. */}
            {images.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                    {images.map((img, idx) => (
                        <div key={img.id || idx} style={{ position: 'relative' }}>
                            <img
                                src={img.thumbnailUrl || img.url || img.src}
                                alt={img.name || `Image ${idx + 1}`}
                                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid #d9d9d9', display: 'block' }}
                            />
                            {!disabled && (
                                <>
                                    <Tooltip title="Crop / reposition">
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<ScissorOutlined />}
                                            onClick={() => setCropping(img)}
                                            style={{ position: 'absolute', bottom: -6, right: -6, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Remove">
                                        <Button
                                            type="text"
                                            danger
                                            size="small"
                                            icon={<CloseOutlined />}
                                            onClick={() => removeImage(img.id)}
                                            style={{ position: 'absolute', top: -6, right: -6, background: '#fff', borderRadius: '50%', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                                        />
                                    </Tooltip>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <ImageCropModal
                open={Boolean(cropping)}
                image={cropping}
                folderPath={captureFolderPath}
                onCancel={() => setCropping(null)}
                onCropped={(fileRef) => replaceImage(cropping.id, fileRef)}
            />

            <Modal title="Image" open={actionOpen} onCancel={() => setActionOpen(false)} footer={null} width={320}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {preview ? (
                        <Button danger onClick={handleRemoveFirst}>
                            Remove
                        </Button>
                    ) : null}
                    <Button onClick={handleUploadFileFromModal} disabled={uploading}>Upload from file</Button>
                    <Button onClick={handleUploadCameraFromModal} disabled={uploading}>Upload from camera</Button>
                    {/* Upload to Drive removed */}
                </div>
            </Modal>



            <Modal title="Camera" open={cameraOpen} onCancel={closeCamera} footer={null} width={600} destroyOnClose>
                <div style={{ textAlign: 'center', margin: 12 }}>
                    <video ref={videoRef} autoPlay playsInline style={{ width: '100%', maxHeight: 400 }} />
                    <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center', gap: 8 }}>
                        <Button type="primary" onClick={captureFromVideo}>
                            Capture
                        </Button>
                        <Button onClick={closeCamera}>Cancel</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default GoldImageCapture;
